import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FileText, CheckCircle, Users,
  RefreshCw, TrendingUp, MapPin, BookOpen
} from 'lucide-react';
import './DashboardView.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://gatepass-api-1753632520.azurewebsites.net/api';

interface DashboardViewProps {
  getAccessToken: () => Promise<string>;
  onNavigation: (view: 'create' | 'view' | 'tasks' | 'reports') => void;
  userInfo?: {
    userId?: string; email?: string; companyId?: number;
    selectedCompanyId?: number; userType?: 'company' | 'gate' | 'admin';
    companyName?: string; userName?: string; userEmail?: string;
  };
}

interface DashboardStats {
  totalRequests: number; approved: number; pending: number;
  rejected: number; returned: number; draft: number;
  cancelled: number; expired: number;
}

interface RegionStat {
  region: string;
  regionId: number | null;
  requestCount: number;
}

interface TimelinePoint {
  timePeriod: string;
  totalCount: number;
  approved: number;
  pending: number;
  rejected: number;
}

// ─── Arc geometry ────────────────────────────────────────────
const toRad = (deg: number) => ((deg - 90) * Math.PI) / 180;
const polar = (cx: number, cy: number, r: number, deg: number) => ({
  x: cx + r * Math.cos(toRad(deg)),
  y: cy + r * Math.sin(toRad(deg)),
});

/**
 * Build an arc path from startDeg → endDeg.
 * Insets each end by `capInset` degrees so the round stroke-linecap
 * stays *inside* the allocated arc, keeping gaps clean.
 */
const arcPath = (
  cx: number, cy: number, r: number,
  startDeg: number, endDeg: number, capInset: number,
) => {
  const s = polar(cx, cy, r, endDeg - capInset);
  const e = polar(cx, cy, r, startDeg + capInset);
  const sweep = endDeg - startDeg - 2 * capInset;
  const large = sweep > 180 ? 1 : 0;
  return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 0 ${e.x} ${e.y}`;
};

// ─── Qatar map (image-based with positioned pin overlays) ───
// Place qatar-map.png in public/assets/qatar-map.png
const QATAR_MAP_SRC = `${process.env.PUBLIC_URL || ''}/assets/qatar-map.png`;

// Pin positions as % of image dimensions (calibrated against Google Maps)
const QATAR_PINS: Record<string, { top: string; left: string }> = {
  'Ras Laffan':  { top: '19%',  left: '83%' },
  'Dukhan':      { top: '38%',  left: '24%' },
  'Doha':        { top: '48%',  left: '76%' },
  'Mesaieed':    { top: '70%',  left: '82%' },
};

const QatarMapImage: React.FC<{ regions: RegionStat[] }> = ({ regions }) => (
  <div className="db-qatar-img-wrap">
    <img src={QATAR_MAP_SRC} alt="Qatar map" className="db-qatar-img" draggable={false} />
    {Object.entries(QATAR_PINS).map(([name, pos]) => {
      const region = regions.find(
        r => r.region.toLowerCase().includes(name.toLowerCase().split(' ')[0])
      );
      const count = region?.requestCount || 0;
      return (
        <div
          key={name}
          className={`db-map-pin ${count > 0 ? 'db-map-pin--active' : ''}`}
          style={{ top: pos.top, left: pos.left }}
          title={`${name}${count > 0 ? ` — ${count} requests` : ''}`}
        >
          <span className="db-map-pin-dot" />
          {count > 0 && <span className="db-map-pin-pulse" />}
          <span className="db-map-pin-label">
            {name}
            {count > 0 && <span className="db-map-pin-count">{count}</span>}
          </span>
        </div>
      );
    })}
  </div>
);

// ─── Main component ──────────────────────────────────────────
export const DashboardView: React.FC<DashboardViewProps> = ({
  getAccessToken, onNavigation, userInfo,
}) => {
  const [statsLoading, setStatsLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    totalRequests: 0, approved: 0, pending: 0, rejected: 0,
    returned: 0, draft: 0, cancelled: 0, expired: 0,
  });
  const [personnelCount, setPersonnelCount] = useState(0);
  const [personnelLoading, setPersonnelLoading] = useState(true);
  const [regionStats, setRegionStats] = useState<RegionStat[]>([]);
  const [timeline, setTimeline] = useState<TimelinePoint[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [hoveredSeg, setHoveredSeg] = useState<string | null>(null);

  const companyId = userInfo?.selectedCompanyId || userInfo?.companyId;

  // ── Fetch overview stats ───────────────────────────────
  const loadDashboardData = useCallback(async () => {
    try {
      setStatsLoading(true); setError(null);
      const token = await getAccessToken();
      const url = new URL(`${API_BASE_URL}/reportsoverview`);
      if (companyId) url.searchParams.append('selectedCompanyId', companyId.toString());
      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.summary) {
          const s = data.summary;
          setStats({
            totalRequests: s.totalRequests || 0, approved: s.approved || 0,
            pending: s.pending || 0, rejected: s.rejected || 0,
            returned: s.returned || 0, draft: s.draft || 0,
            cancelled: s.cancelled || 0, expired: s.expired || 0,
          });
        }
      } else throw new Error('Failed');
    } catch (err) {
      console.error('Dashboard error:', err);
      setError('Failed to load dashboard statistics');
    } finally { setStatsLoading(false); }
  }, [getAccessToken, companyId]);

  // ── Fetch personnel count ──────────────────────────────
  const loadPersonnelCount = useCallback(async () => {
    try {
      setPersonnelLoading(true);
      const token = await getAccessToken();
      const url = new URL(`${API_BASE_URL}/personnel`);
      if (companyId) url.searchParams.append('selectedCompanyId', companyId.toString());
      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success)
          setPersonnelCount(Array.isArray(data.data) ? data.data.length : data.totalCount || 0);
      }
    } catch (err) { console.error('Personnel error:', err); }
    finally { setPersonnelLoading(false); }
  }, [getAccessToken, companyId]);

  // ── Fetch region + timeline from monthly report ─────
  const loadRegionStats = useCallback(async () => {
    try {
      const token = await getAccessToken();
      const url = new URL(`${API_BASE_URL}/reportsmonthly`);
      if (companyId) url.searchParams.append('selectedCompanyId', companyId.toString());
      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          if (data.regionDistribution) setRegionStats(data.regionDistribution);
          if (data.timeline) setTimeline(data.timeline);
        }
      }
    } catch (err) { console.error('Region/timeline error:', err); }
  }, [getAccessToken, companyId]);

  useEffect(() => {
    loadDashboardData();
    loadPersonnelCount();
    loadRegionStats();
  }, [loadDashboardData, loadPersonnelCount, loadRegionStats]);

  // ── Derived values ─────────────────────────────────────

  const isCompanyUser = userInfo?.userType === 'company';
  const approvalRate = stats.totalRequests > 0
    ? ((stats.approved / stats.totalRequests) * 100).toFixed(1) : '0';

  // ── Chart line data (SVG polyline) ─────────────────────
  const CHART_W = 400, CHART_H = 120, CHART_PAD = 30;
  const chartPoints = useMemo(() => {
    if (timeline.length === 0) return { line: '', area: '', labels: [] as { x: number; label: string }[] };
    const maxVal = Math.max(...timeline.map(t => t.totalCount), 1);
    const xStep = (CHART_W - CHART_PAD * 2) / Math.max(timeline.length - 1, 1);
    const pts = timeline.map((t, i) => ({
      x: CHART_PAD + i * xStep,
      y: CHART_H - CHART_PAD - ((t.totalCount / maxVal) * (CHART_H - CHART_PAD * 2)),
      count: t.totalCount,
      date: t.timePeriod,
    }));
    // Build smooth cubic Bézier spline (Catmull-Rom → Bézier)
    const tension = 0.3; // 0 = sharp, 1 = very smooth
    let line = `M ${pts[0].x} ${pts[0].y}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[Math.max(i - 1, 0)];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[Math.min(i + 2, pts.length - 1)];
      const cp1x = p1.x + (p2.x - p0.x) * tension;
      const cp1y = p1.y + (p2.y - p0.y) * tension;
      const cp2x = p2.x - (p3.x - p1.x) * tension;
      const cp2y = p2.y - (p3.y - p1.y) * tension;
      line += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
    }
    // Filled area path (closes to bottom)
    const area = line +
      ` L ${pts[pts.length - 1].x} ${CHART_H - CHART_PAD}` +
      ` L ${pts[0].x} ${CHART_H - CHART_PAD} Z`;
    // X-axis labels (show first, middle, last)
    const labels: { x: number; label: string }[] = [];
    const fmtDate = (d: string) => {
      const dt = new Date(d);
      return isNaN(dt.getTime()) ? d : `${dt.getDate()}/${dt.getMonth() + 1}`;
    };
    if (pts.length >= 1) labels.push({ x: pts[0].x, label: fmtDate(pts[0].date) });
    if (pts.length >= 3) labels.push({ x: pts[Math.floor(pts.length / 2)].x, label: fmtDate(pts[Math.floor(pts.length / 2)].date) });
    if (pts.length >= 2) labels.push({ x: pts[pts.length - 1].x, label: fmtDate(pts[pts.length - 1].date) });
    return { line, area, labels, pts };
  }, [timeline]);

  // ── Arc gauge computation ──────────────────────────────
  // Horseshoe opens at the bottom (gap centered on 180°/6 o'clock)
  const CX = 150, CY = 130, R = 110;
  const ARC_START = 225; // 7:30 position (lower-left)
  const ARC_SWEEP = 270; // ends at 135° (4:30, lower-right)
  const STROKE = 28;
  const GAP_DEG = 10; // visible gap between segments
  // How many degrees the round linecap extends past the path endpoint
  const CAP_INSET = ((STROKE / 2) / (2 * Math.PI * R)) * 360;

  const segmentDefs = useMemo(() => [
    { key: 'approved', label: 'Approved', value: stats.approved, color: '#22c55e', dim: '#a7e8c3', bright: '#16a34a' },
    { key: 'pending',  label: 'Pending',  value: stats.pending,  color: '#f59e0b', dim: '#fcd68d', bright: '#d97706' },
    { key: 'rejected', label: 'Rejected', value: stats.rejected, color: '#ef4444', dim: '#f9a8a8', bright: '#dc2626' },
    { key: 'returned', label: 'Returned', value: stats.returned, color: '#f97316', dim: '#fdba8c', bright: '#ea580c' },
    { key: 'draft',    label: 'Draft',    value: stats.draft,    color: '#94a3b8', dim: '#cbd5e1', bright: '#64748b' },
  ], [stats]);

  const activeSegs = segmentDefs.filter(s => s.value > 0);
  const segTotal = activeSegs.reduce((a, s) => a + s.value, 0);

  const arcSegments = useMemo(() => {
    if (segTotal === 0) return [];
    const gaps = activeSegs.length > 1 ? GAP_DEG * (activeSegs.length - 1) : 0;
    const usable = ARC_SWEEP - gaps;
    let cursor = ARC_START;
    return activeSegs.map((seg, i) => {
      const sweep = Math.max((seg.value / segTotal) * usable, 8);
      const start = cursor;
      const end = cursor + sweep;
      cursor = end + (i < activeSegs.length - 1 ? GAP_DEG : 0);
      // Midpoint for tooltip position
      const midDeg = (start + end) / 2;
      const tip = polar(CX, CY, R + 28, midDeg); // position outside the arc
      const pct = ((seg.value / stats.totalRequests) * 100).toFixed(1);
      return { ...seg, d: arcPath(CX, CY, R, start, end, CAP_INSET), tipX: tip.x, tipY: tip.y, pct };
    });
  }, [activeSegs, segTotal, CAP_INSET, stats.totalRequests]);

  // Approval ring
  const RING_R = 56, RING_CIRC = 2 * Math.PI * RING_R;
  const ringOffset = RING_CIRC - (parseFloat(approvalRate) / 100) * RING_CIRC;

  // Known operational regions (fallback when API data unavailable)
  const displayRegions = useMemo(() => {
    const knownRegions = ['Ras Laffan', 'Doha', 'Mesaieed', 'Dukhan'];
    if (regionStats.length > 0) {
      return regionStats.map(r => ({
        name: r.region || 'Not Specified',
        count: r.requestCount,
      }));
    }
    return knownRegions.map(name => ({ name, count: 0 }));
  }, [regionStats]);

  return (
   
    <>
      <h1 style={{
        fontSize: '25px',
        fontWeight: 600,
        color: '#1a1a1a',
        margin: '0 0 12px 0',
        padding: '5px 0 0 20px',
        lineHeight: 1.3,
        fontFamily: "'Segoe UI', sans-serif",
        letterSpacing: '-0.01em',
      }}>
        Dashboard
      </h1>
      <div className="db-container">

      {/* Error */}
      {error && (
        <div className="db-error" role="alert">
          <span>⚠ {error}</span>
          <button className="db-error-retry" onClick={loadDashboardData}>
            <RefreshCw size={13} /> Retry
          </button>
          <button className="db-error-close" onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* ─── Bento Grid ─── */}
      <div className="db-bento">

        {/* ─── Card 1: Arc Gauge ─── */}
        <div className="db-card db-card--arc">
          <svg viewBox="-10 -40 320 280" className="db-arc-svg">
            {/* Background track */}
            <path
              d={arcPath(CX, CY, R, ARC_START, ARC_START + ARC_SWEEP, 0)}
              fill="none" stroke="#e8ecf1" strokeWidth={STROKE}
              strokeLinecap="round"
            />
            {/* Colored segments — dim by default, bright on hover */}
            {arcSegments.map((seg, i) => {
              const isHovered = hoveredSeg === seg.key;
              const hasHover = hoveredSeg !== null;
              return (
                <path
                  key={seg.key}
                  d={seg.d}
                  fill="none"
                  stroke={isHovered ? seg.bright : seg.dim}
                  strokeWidth={isHovered ? STROKE + 6 : STROKE}
                  strokeLinecap="round"
                  opacity={hasHover && !isHovered ? 0.5 : 1}
                  className="db-arc-seg"
                  style={{
                    animationDelay: `${i * 0.15}s`,
                    cursor: 'pointer',
                    transition: 'stroke-width 0.25s, stroke 0.25s, opacity 0.25s, filter 0.25s',
                    filter: isHovered ? 'drop-shadow(0 2px 8px rgba(0,0,0,0.18))' : 'none',
                  }}
                  onMouseEnter={() => setHoveredSeg(seg.key)}
                  onMouseLeave={() => setHoveredSeg(null)}
                />
              );
            })}
            {/* Hover tooltip bubble — clamped to stay inside viewBox */}
            {hoveredSeg && arcSegments.map(seg => seg.key === hoveredSeg ? (
              <g key={`tip-${seg.key}`} className="db-arc-tooltip">
                <rect
                  x={Math.max(-5, Math.min(seg.tipX - 36, 240))}
                  y={Math.max(-35, seg.tipY - 20)}
                  width="72" height="30" rx="7"
                  fill="#1a202c" opacity="0.92"
                />
                <text
                  x={Math.max(31, Math.min(seg.tipX, 276))}
                  y={Math.max(-16, seg.tipY - 2)}
                  textAnchor="middle" dominantBaseline="central"
                  fill="white" fontSize="11.5" fontWeight="700"
                  fontFamily="'Segoe UI', sans-serif"
                >
                  {seg.value} · {seg.pct}%
                </text>
              </g>
            ) : null)}
            {/* Center text */}
            <text x={CX} y={CY - 14} textAnchor="middle" className="db-arc-num">
              {statsLoading ? '—' : stats.totalRequests}
            </text>
            <text x={CX} y={CY + 10} textAnchor="middle" className="db-arc-lbl">
              Total Requests
            </text>
          </svg>

          <div className="db-arc-legend">
            {segmentDefs.map(s => {
              const isActive = hoveredSeg === s.key;
              return (
                <div
                  className={`db-leg ${isActive ? 'db-leg--active' : ''}`}
                  key={s.key}
                  onMouseEnter={() => setHoveredSeg(s.key)}
                  onMouseLeave={() => setHoveredSeg(null)}
                >
                  <span className="db-leg-dot" style={{ background: isActive ? s.bright : s.dim, transition: 'background 0.25s' }} />
                  <span className="db-leg-text">{s.label}</span>
                  <span className="db-leg-val">{statsLoading ? '—' : s.value}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ─── Card 2: Approval Rate ─── */}
        <div className="db-card db-card--rate">
          <span className="db-card-label">APPROVAL RATE</span>
          <div className="db-ring-wrap">
            <svg viewBox="0 0 140 140" className="db-ring-svg">
              <circle cx="70" cy="70" r={RING_R}
                fill="none" stroke="#e8ecf1" strokeWidth="10" />
              <circle cx="70" cy="70" r={RING_R}
                fill="none" stroke="#22c55e" strokeWidth="10" strokeLinecap="round"
                strokeDasharray={RING_CIRC} strokeDashoffset={ringOffset}
                transform="rotate(-90 70 70)" className="db-ring-fill" />
              <text x="70" y="65" textAnchor="middle" className="db-ring-num">
                {statsLoading ? '—' : approvalRate}
              </text>
              <text x="70" y="83" textAnchor="middle" className="db-ring-pct">%</text>
            </svg>
          </div>
          <div className="db-rate-detail">
            <div className="db-rate-row">
              <CheckCircle size={14} color="#22c55e" />
              <span>{stats.approved} approved</span>
            </div>
            <div className="db-rate-row">
              <FileText size={14} color="#64748b" />
              <span>{stats.totalRequests} total</span>
            </div>
          </div>
          <div className="db-rate-personnel">
            <Users size={16} />
            <div>
              <span className="db-rate-pval">{personnelLoading ? '—' : personnelCount}</span>
              <span className="db-rate-plbl">Personnel Registered</span>
            </div>
          </div>
        </div>

        {/* ─── Card 3: Requests Timeline Chart ─── */}
        <div className="db-card db-card--chart">
          <div className="db-chart-header">
            <span className="db-card-label">REQUESTS THIS MONTH</span>
            <span className="db-chart-total">
              <TrendingUp size={14} />
              {stats.totalRequests} total
            </span>
          </div>
          {timeline.length > 0 && chartPoints.line ? (
            <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} className="db-chart-svg" preserveAspectRatio="none">
              <defs>
                <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22c55e" stopOpacity="0.25" />
                  <stop offset="100%" stopColor="#22c55e" stopOpacity="0.02" />
                </linearGradient>
              </defs>
              {/* Grid lines */}
              {[0.25, 0.5, 0.75].map(pct => (
                <line key={pct}
                  x1={CHART_PAD} y1={CHART_H - CHART_PAD - pct * (CHART_H - CHART_PAD * 2)}
                  x2={CHART_W - CHART_PAD} y2={CHART_H - CHART_PAD - pct * (CHART_H - CHART_PAD * 2)}
                  stroke="#e8ecf1" strokeWidth="0.5"
                />
              ))}
              {/* Filled area */}
              <path d={chartPoints.area} fill="url(#chartGrad)" />
              {/* Line */}
              <path d={chartPoints.line} fill="none" stroke="#22c55e" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round" />
              {/* Data dots */}
              {chartPoints.pts?.map((p, i) => (
                <circle key={i} cx={p.x} cy={p.y} r="3"
                  fill="#fff" stroke="#22c55e" strokeWidth="2" />
              ))}
              {/* X-axis labels */}
              {chartPoints.labels.map((l, i) => (
                <text key={i} x={l.x} y={CHART_H - 6}
                  textAnchor="middle" fontSize="9" fill="#94a3b8"
                  fontFamily="'Segoe UI', sans-serif" fontWeight="500">
                  {l.label}
                </text>
              ))}
            </svg>
          ) : (
            <div className="db-chart-empty">
              <TrendingUp size={28} />
              <span>Chart data loading...</span>
            </div>
          )}
        </div>

        {/* ─── Card 4: Regions + Qatar Map ─── */}
        <div className="db-card db-card--regions">
          <span className="db-card-label">REGIONS</span>
          <div className="db-regions-layout">
            <div className="db-regions-list">
              {displayRegions.map((r, i) => (
                <div className="db-region-row" key={r.name} style={{ animationDelay: `${i * 0.05}s` }}>
                  <MapPin size={15} className="db-region-pin" />
                  <div className="db-region-info">
                    <span className="db-region-name">{r.name}</span>
                    {r.count > 0 && (
                      <span className="db-region-count">{r.count} requests</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="db-qatar-wrap">
              <QatarMapImage regions={regionStats} />
            </div>
          </div>
        </div>
      </div>

      {/* Onboard strip */}
      {isCompanyUser && stats.totalRequests === 0 && !statsLoading && (
        <div className="db-onboard">
          <BookOpen size={18} className="db-onboard-icon" />
          <div className="db-onboard-text">
            <strong>Get started</strong> — Use <em>Create Request</em> from the sidebar to submit your first gate pass application.
          </div>
        </div>
      )}
    </div>
    </>
  );
};